import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import Button from './Button';

// =============================================================================
// Dropdown — the app's first (and only) menu primitive (Sep 2026).
//
// Three trigger shapes, one menu:
//   1. Default: a Button (label/icon/variant) with a trailing chevron.
//   2. splitAction: a split button — main action segment + chevron segment
//      sharing one visual pill (the Solve button).
//   3. renderTrigger: fully custom trigger (PeriodNav's range label).
//
// Menu items: { id, label, icon?, sublabel?, checked?, badge?, badgeTone?,
//               danger?, disabled?, onSelect }
//
// Click-outside (pointerdown) + Escape close. Menu sits at z-[200]: above
// cards and the roster grid's sticky cells (z-10), below every modal
// (z-[9999]) and Drawer (z-[10000]).
//
// STYLING HAZARD (design-tokens.css): `button { border-radius !important }`
// beats both Tailwind classes and inline styles, so the split button's inner
// corners cannot be squared per-segment. Instead both segments sit inside an
// overflow-hidden wrapper that carries the radius and a matching background,
// which fills the notches the segments' forced radii would otherwise leave.
// =============================================================================

const Dropdown = ({
  // Default trigger (ignored when renderTrigger or splitAction handles it)
  label,
  icon: Icon,
  variant = 'secondary',
  size,
  triggerClassName = '',
  disabled = false,
  showChevron = true,
  dot = false,                 // red attention dot on the trigger's corner
  // Split-button mode
  splitAction = null,          // { label, icon, onClick, variant, disabled, isLoading, className }
  // Custom trigger
  renderTrigger = null,        // ({ open, toggle }) => JSX
  // Menu
  items = [],
  align = 'left',
  menuClassName = '',
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = () => { if (!disabled) setOpen((o) => !o); };

  const select = (item) => {
    setOpen(false);
    if (item.onSelect) item.onSelect();
  };

  const menu = open && (
    <div
      className={`absolute top-full mt-2 ${align === 'right' ? 'right-0' : 'left-0'} min-w-[220px] z-[200] bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 animate-in fade-in zoom-in-95 duration-150 ${menuClassName}`}
      role="menu"
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          onClick={() => select(item)}
          className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs font-bold text-left disabled:opacity-40 disabled:cursor-not-allowed ${item.danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-700 hover:bg-slate-50'}`}
          style={{ background: 'transparent', boxShadow: 'none' }}
        >
          {item.icon && <item.icon className={`w-3.5 h-3.5 flex-shrink-0 ${item.danger ? 'text-rose-500' : 'text-slate-400'}`} />}
          <span className="flex-1 min-w-0">
            <span className="block truncate">{item.label}</span>
            {item.sublabel && (
              <span className="block text-[9px] font-bold text-slate-400 normal-case tracking-normal">
                {item.sublabel}
              </span>
            )}
          </span>
          {item.badge !== undefined && item.badge !== null && (
            <span className={`flex-shrink-0 text-[9px] font-bold tabular-nums rounded-full px-1.5 py-0.5 ${item.badgeTone === 'danger' ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-500'}`}>
              {item.badge}
            </span>
          )}
          {item.checked && <Check className="w-3.5 h-3.5 flex-shrink-0 text-[var(--primary)]" />}
        </button>
      ))}
    </div>
  );

  const attentionDot = dot && (
    <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-rose-500 border-2 border-white pointer-events-none" />
  );

  // --- Split-button form -----------------------------------------------------
  if (splitAction) {
    const segVariant = splitAction.variant || 'primary';
    // The wrapper's background fills the notches the segments' forced radii
    // leave at the joint — it must match the segment variant's own ground
    // (a terracotta fill behind white secondary segments would bleed through).
    const wrapBg = {
      primary: 'bg-[var(--primary)]', accent: 'bg-[#042f2e]',
      danger: 'bg-[var(--danger)]', secondary: 'bg-white', success: 'bg-emerald-50',
    }[segVariant] || 'bg-[var(--primary)]';
    const divider = (segVariant === 'secondary' || segVariant === 'success')
      ? 'border-l border-slate-200' : 'border-l border-white/20';
    return (
      <div ref={rootRef} className="relative inline-flex">
        <div
          className={`inline-flex items-stretch overflow-hidden ${wrapBg}`}
          style={{ borderRadius: 'var(--radius-md)' }}
        >
          <Button
            variant={segVariant}
            icon={splitAction.icon}
            isLoading={splitAction.isLoading}
            disabled={splitAction.disabled}
            onClick={splitAction.onClick}
            className={`shadow-none ${splitAction.className || ''}`}
          >
            {splitAction.label}
          </Button>
          <Button
            variant={segVariant}
            icon={ChevronDown}
            disabled={disabled}
            onClick={toggle}
            aria-label="More options"
            aria-expanded={open}
            className={`shadow-none ${divider}`}
            style={{ paddingLeft: 8, paddingRight: 8 }}
          />
        </div>
        {attentionDot}
        {menu}
      </div>
    );
  }

  // --- Custom trigger form ---------------------------------------------------
  if (renderTrigger) {
    return (
      <div ref={rootRef} className="relative inline-flex">
        {renderTrigger({ open, toggle })}
        {attentionDot}
        {menu}
      </div>
    );
  }

  // --- Default Button trigger ------------------------------------------------
  return (
    <div ref={rootRef} className="relative inline-flex">
      <Button
        variant={variant}
        size={size}
        icon={Icon}
        disabled={disabled}
        onClick={toggle}
        aria-expanded={open}
        className={triggerClassName}
      >
        {label}
        {showChevron && <ChevronDown className="w-3 h-3 opacity-60" />}
      </Button>
      {attentionDot}
      {menu}
    </div>
  );
};

export default Dropdown;
